import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';

import { SAMPLE_RANDOM, previewResultTemplate } from '../../../common/resultTemplateApi.js';

// The station's own viewport, per the meta tag in result.html. The frame is rendered at
// exactly this size and scaled down as a whole, so what overflows here overflows in the
// field -- laying it out at the form's width instead would preview a screen that does not
// exist.
const KIOSK_WIDTH = 800;
const KIOSK_HEIGHT = 480;
const SCALE = 0.5;

// Long enough that typing in the editor dialog does not fire a request per keystroke, short
// enough that the section is up to date by the time the dialog is closed and read.
const REFRESH_DELAY_MS = 700;

// `action` is rendered in a bar directly above the frame and right-aligned to it, so the
// button that edits this screen sits against the screen it edits rather than out at the
// form column's edge, which is more than twice as wide.
export default function ResultTemplateInlinePreview({ wasteReceiverId, html, css, action = null }) {
    const [renderedHtml, setRenderedHtml] = useState('');
    const [selection, setSelection] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    // A slower response must not land after a newer one and overwrite it.
    const requestIdRef = useRef(0);

    const loadPreview = useCallback(async () => {
        if (!wasteReceiverId) return;

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setBusy(true);

        try {
            const result = await previewResultTemplate({
                wasteReceiverId,
                html,
                css,
                sample: SAMPLE_RANDOM,
            });

            if (requestIdRef.current !== requestId) return;
            setRenderedHtml(result.rendered_html);
            setSelection(result.selection);
            setError('');
        } catch (requestError) {
            if (requestIdRef.current !== requestId) return;
            // The editor dialog reports errors with line numbers; here one line is enough to
            // say the screen cannot be drawn and send the reader to Rediger.
            const [firstError] = requestError.errors || [];
            setRenderedHtml('');
            setError(firstError ? firstError.message : requestError.message);
        } finally {
            if (requestIdRef.current === requestId) setBusy(false);
        }
    }, [wasteReceiverId, html, css]);

    useEffect(() => {
        const timer = setTimeout(loadPreview, REFRESH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [loadPreview]);

    // The frame's on-screen size is the one measurement the action bar has to share, so it
    // is published as a custom property rather than repeated.
    const previewSizing = {
        '--inline-preview-width': `${KIOSK_WIDTH * SCALE}px`,
        '--inline-preview-height': `${KIOSK_HEIGHT * SCALE}px`,
    };

    if (!wasteReceiverId) {
        return (
            <div className="result-template-inline-preview" style={previewSizing}>
                {action ? <div className="result-template-inline-actions">{action}</div> : null}
                <p className="setup-form-section-note">
                    Gem affaldsmodtageren én gang, så vises resultatskærmen her.
                </p>
            </div>
        );
    }

    return (
        <div className="result-template-inline-preview" style={previewSizing}>
            {action ? <div className="result-template-inline-actions">{action}</div> : null}
            <div className="result-template-inline-frame-box">
                {renderedHtml ? (
                    <iframe
                        title="Resultatskærm, eksempel"
                        className="result-template-inline-frame"
                        width={KIOSK_WIDTH}
                        height={KIOSK_HEIGHT}
                        style={{ transform: `scale(${SCALE})` }}
                        // allow-scripts without allow-same-origin: the template's own script
                        // runs, so an error in it shows here rather than in the field, and it
                        // reaches nothing of this app. Sound is off server-side.
                        sandbox="allow-scripts"
                        srcDoc={renderedHtml}
                    />
                ) : (
                    <div className="result-template-inline-placeholder">
                        {busy ? <CircularProgress size={20} /> : null}
                    </div>
                )}
            </div>

            {error ? (
                <Alert severity="error" className="result-template-inline-alert">
                    {error}
                </Alert>
            ) : (
                <p className="setup-form-section-note">
                    {selection
                        ? `Tilfældig række ${selection.row_index} fra sorteringslisten.`
                        : 'Henter et eksempel fra sorteringslisten…'}
                </p>
            )}
        </div>
    );
}
